import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const LAST_VISITED_URL_KEY = 'firmware-manager:last-visited-url';
const MAX_URL_LENGTH = 500;

type LocationLike = {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
};

const isSafeInternalUrl = (value: unknown): value is string => {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > MAX_URL_LENGTH ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    typeof window === 'undefined'
  ) {
    return false;
  }

  try {
    const parsed = new URL(value, window.location.origin);
    const isLoginPath =
      parsed.pathname === '/login' ||
      parsed.pathname.startsWith('/login/');

    return (
      parsed.origin === window.location.origin &&
      parsed.pathname !== '/' &&
      !isLoginPath
    );
  } catch {
    return false;
  }
};

const getStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const locationToUrl = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return isSafeInternalUrl(value) ? value : null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const location = value as LocationLike;
  if (typeof location.pathname !== 'string') {
    return null;
  }

  const search =
    typeof location.search === 'string' ? location.search : '';
  const hash = typeof location.hash === 'string' ? location.hash : '';
  const url = `${location.pathname}${search}${hash}`;

  return isSafeInternalUrl(url) ? url : null;
};

export const saveLastVisitedUrl = (url: string): void => {
  if (!isSafeInternalUrl(url)) {
    return;
  }

  try {
    getStorage()?.setItem(LAST_VISITED_URL_KEY, url);
  } catch {
    // Storage is optional. Authentication falls back to router state/defaults.
  }
};

export const getLastVisitedUrl = (): string | null => {
  try {
    const savedUrl = getStorage()?.getItem(LAST_VISITED_URL_KEY);
    return locationToUrl(savedUrl);
  } catch {
    return null;
  }
};

export const clearLastVisitedUrl = (): void => {
  try {
    getStorage()?.removeItem(LAST_VISITED_URL_KEY);
  } catch {
    // Storage is optional.
  }
};

export const resolvePostLoginUrl = (from: unknown): string => {
  return locationToUrl(from) ?? getLastVisitedUrl() ?? '/products';
};

export const useSaveLastVisitedUrl = (): void => {
  const location = useLocation();

  useEffect(() => {
    saveLastVisitedUrl(
      `${location.pathname}${location.search}${location.hash}`,
    );
  }, [location.pathname, location.search, location.hash]);
};
